import { useCallback, useEffect, useState } from "react";
import { Form, Input, Card, Typography, Row, Col, Checkbox } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { MODULES } from "./Permissions";
import PermissionModuleRow from "./PermissionModuleRow";
import { api } from "../../utils/apiClient";
import { toast } from "react-toastify";
import Breadcrumb from "../../components/Breadcrumb";
import { useDispatch, useSelector } from "react-redux";
import { setPermissions } from "../../redux/slice/permissions";
import ContentLoader from "../../utils/ContentLoader";

const { Text } = Typography;

const RoleForm = ({ mode }) => {
  const profile = useSelector((state) => state.profile.profile) || null;
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const isEdit = mode === "edit";

  useEffect(() => {
    if (!isEdit || !id) return;

    const loadRole = async () => {
      try {
        const { data } = await api.get(`/roles/${id}`);
        const role = data?.data;
        const selected = role?.permissions?.map((p) => p.name) || [];

        form.setFieldsValue({
          name: role?.name || "",
          allPermissions:
            selected.length === MODULES.flatMap((m) => m.permissions.map((p) => p.value)).length,
        });
        setSelectedPermissions(selected);
      } catch {
        toast.error("Failed to load role data");
      }
    };

    loadRole();
  }, [id, isEdit, form]);

  const handleGlobalSelectAll = (e) => {
    const checked = e.target.checked;
    const allPerms = MODULES.flatMap((m) => m.permissions.map((p) => p.value));
    setSelectedPermissions(checked ? allPerms : []);
    const updateValues = {};
    MODULES.forEach((mod) => {
      updateValues[`${mod.key}All`] = checked;
      mod.permissions.forEach((p) => (updateValues[p] = checked));
    });
    form.setFieldsValue(updateValues);
  };

  const handleModuleSelectAll = useCallback(
    (moduleKey, permissions) => (e) => {
      const checked = e.target.checked;
      setSelectedPermissions((prev) => {
        let updated = [...prev];
        if (checked) permissions.forEach((p) => { if (!updated.includes(p)) updated.push(p); });
        else updated = updated.filter((p) => !permissions.includes(p));
        return updated;
      });
      const update = {};
      permissions.forEach((p) => (update[p] = checked));
      update[`${moduleKey}All`] = checked;
      form.setFieldsValue(update);
    },
    [form]
  );

  const handleIndividualPermissionChange = useCallback(
    (moduleKey, permissions) => (e, perm) => {
      const checked = e.target.checked;
      setSelectedPermissions((prev) => {
        const updated = checked ? [...prev, perm] : prev.filter((p) => p !== perm);
        return updated;
      });
      const formValues = form.getFieldsValue();
      const allChecked = permissions.every((p) => formValues[p] || (p === perm ? checked : false));
      form.setFieldsValue({ [`${moduleKey}All`]: allChecked });

      const allModulesChecked = MODULES.every((mod) =>
        mod.permissions.every((p) => formValues[p] || selectedPermissions.includes(p))
      );
      form.setFieldsValue({ allPermissions: allModulesChecked });
    },
    [form, selectedPermissions]
  );

  const onFinish = async (values) => {
    if (!values.name?.trim()) return toast.error("Role name is required.");
    if (selectedPermissions.length === 0) return toast.warn("Please select at least one permission.");
    const payload = {
      name: values.name,
      permissions: selectedPermissions,
    };
    try {
      setLoading(true);
      const url = isEdit ? `/roles/${id}` : `/roles`;
      const method = isEdit ? api.put : api.post;
      const resp = await method(url, payload);
      const data = resp?.data?.data
      const perms = data.permissions?.map((p) => p.name) || [];
      data.id === profile?.id && dispatch(setPermissions(perms));
      toast.success(isEdit ? "Role updated successfully!" : "Role created successfully!");
      navigate(-1);
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <ContentLoader />;
  return (
    <>
      <Breadcrumb title="Role Access Management" />

      <div style={{ marginTop: "10px" }}>
        <Card title={<div style={{ fontSize: "1.200rem", fontWeight: "640" }}>{isEdit ? "Edit Role" : "Create Role"}</div >} className="mx-auto" variant="borderless" >
          <Form form={form} layout="vertical" onFinish={onFinish}>
            <Row gutter={16}>
              <Col xs={24} sm={24} md={12} lg={8}>
                <Form.Item label="Role Name" name="name">
                  <Input placeholder="Enter role name" size="large" className="custom-input-height" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Text>Assign Permissions</Text>
                <div style={{ marginLeft: 16 }}>
                  <Form.Item name="allPermissions" valuePropName="checked" noStyle>
                    <Checkbox onChange={handleGlobalSelectAll}>All</Checkbox>
                  </Form.Item>
                </div>
              </div>
            </Form.Item>
            <div className="mobile-scroll">
              <Card size="small" style={{ marginBottom: 24, border: "1px solid #f0f0f0" }}>
                {MODULES.map((module) => (
                  <PermissionModuleRow
                    key={module.key}
                    module={module}
                    handleModuleSelectAll={handleModuleSelectAll}
                    handleIndividualPermissionChange={handleIndividualPermissionChange}
                    selectedPermissions={selectedPermissions}
                  />
                ))}
              </Card>
            </div>
            <div className="mt-10 mb-10 d-flex justify-content-end">
              <button
                className="btn btn-primary-600 px-5"
                style={{ borderRadius: "4px" }}
              >
                {isEdit ? "Update" : "Submit"}
              </button>
            </div>
          </Form>
        </Card >
      </div>
    </>
  );
};

export default RoleForm;